import { Theme, createTheme } from '@mui/material';
import { getServiceName } from '@repo/utils';

import { affiliateThemeColor } from './affiliate';
import { chatBaseTheme } from './chat';
import { chatServiceColor } from './chatServiceColor';
import { commonTheme } from './common';
import { ThemeConfigType, muiBaseOptions, muiThemesOptions } from './mui';
import { tapTheme } from './tap';
import { TingleThemeType, tingleTheme } from './tingle';
import { SERVICE } from '../constants/service';

// BubbleTap / Bubble Studio Theme
export { tapTheme as defaultTheme } from './tap';
export type { TapBaseThemeType } from './tap';

// Chat Affiliate Theme
export { affiliateThemeColor } from './affiliate';

// Chat Theme
export { chatThemeColor } from './chat';
// export type { ChatBaseThemeType } from './chat';

// Tingle Theme
export { tingleTheme, tingleColor } from './tingle';
export type { TingleThemeType } from './tingle';

// export const themeByService = () => {
//   const themes: Record<string, ChatBaseThemeType | TingleThemeType> = {
//     bubblechat: chatBaseTheme,
//     tingle: tingleTheme,
//   };
//   const serviceName = (import.meta.env.VITE_APP_SERVICE_NAME || 'bubblechat') as string;
//   return themes[serviceName];
// };

type ThemeColor = {
  role: string;
  sub_description?: string;
  light: string;
  dark: string;
};

type RecursiveTheme<T> = {
  [K in keyof T]: T[K] extends { role: string } ? ThemeColor : T[K] extends object ? RecursiveTheme<T[K]> : T[K];
};

type ThemeColorWithService = {
  role: string;
  sub_description?: string;
  bubblechat: { light: string; dark: string };
  tingle: { light: string; dark: string };
};

type ChatColorTheme = RecursiveTheme<typeof chatServiceColor>;
type AffiliateColorTheme = RecursiveTheme<typeof affiliateThemeColor>;

const getColorByService = (serviceName: 'bubblechat' | 'tingle'): ChatColorTheme => {
  const isThemeColorWithService = (val: any): val is ThemeColorWithService =>
    val && typeof val === 'object' && 'role' in val && 'bubblechat' in val && 'tingle' in val;

  const extractTheme = <T extends object>(obj: T): RecursiveTheme<T> => {
    return Object.keys(obj).reduce((acc, key) => {
      const value = obj[key as keyof T];

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        if (isThemeColorWithService(value)) {
          acc[key as keyof T] = {
            role: value.role,
            sub_description: value.sub_description ?? '',
            light: value[serviceName].light,
            dark: value[serviceName].dark,
          } as RecursiveTheme<T>[keyof T];
        } else {
          acc[key as keyof T] = extractTheme(value) as RecursiveTheme<T>[keyof T];
        }
      } else {
        acc[key as keyof T] = value as RecursiveTheme<T>[keyof T];
      }

      return acc;
    }, {} as RecursiveTheme<T>);
  };

  return extractTheme(chatServiceColor);
};

export const themeByService = () => {
  const serviceName = (import.meta.env.VITE_APP_SERVICE_NAME || 'bubblechat') as 'bubblechat' | 'tingle';
  const theme = {
    ...commonTheme,
    color: {
      ...tapTheme.color,
      ...getColorByService(serviceName),
      rgba: {
        rgba1: 'rgba(255, 255, 255, 0)',
        rgba2: 'rgba(255, 255, 255, 0.20)',
        rgba3: 'rgba(255, 255, 255, 0.20)',
      },
    },
  };

  return theme;
};

export type ChatBaseThemeType = ReturnType<typeof themeByService>;

const getAffiliateColorByService = (serviceName: 'bubblechat' | 'tingle'): AffiliateColorTheme => {
  const isThemeColorWithService = (val: any): val is ThemeColorWithService =>
    val && typeof val === 'object' && 'role' in val && 'bubblechat' in val && 'tingle' in val;

  const extractTheme = <T extends object>(obj: T): RecursiveTheme<T> => {
    return Object.keys(obj).reduce((acc, key) => {
      const value = obj[key as keyof T];

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        if (isThemeColorWithService(value)) {
          acc[key as keyof T] = {
            role: value.role,
            sub_description: value.sub_description ?? '',
            light: value[serviceName].light,
            dark: value[serviceName].dark,
          } as RecursiveTheme<T>[keyof T];
        } else {
          acc[key as keyof T] = extractTheme(value) as RecursiveTheme<T>[keyof T];
        }
      } else {
        acc[key as keyof T] = value as RecursiveTheme<T>[keyof T];
      }

      return acc;
    }, {} as RecursiveTheme<T>);
  };

  return extractTheme(affiliateThemeColor);
};

export const affiliateThemeByService = () => {
  const serviceName = (import.meta.env.VITE_APP_SERVICE_NAME || 'bubblechat') as 'bubblechat' | 'tingle';
  const theme = {
    ...commonTheme,
    color: {
      ...getAffiliateColorByService(serviceName),
      rgba: {
        rgba1: 'rgba(255, 255, 255, 0)',
        rgba2: 'rgba(255, 255, 255, 0.20)',
        rgba3: 'rgba(255, 255, 255, 0.20)',
      },
    },
  };

  return theme;
};

export type AffiliateBaseThemeType = ReturnType<typeof affiliateThemeByService>;

export const muiTheme = (config: ThemeConfigType = {}): Theme => {
  const serviceName = getServiceName();
  let themeOptions = muiThemesOptions[serviceName];

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    themeOptions = muiThemesOptions[serviceName];
  }

  let theme = createTheme(muiBaseOptions, themeOptions);

  return theme;
};
